import React, { useState, useRef, useEffect } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { Checkbox } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import "../assets/full-calendar.css";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Asia/Kolkata");

const ScheduleCalendar = ({
  enableEdit,
  sequences,
  selectedSequenceId,
  events,
  setEvents,
}) => {
  const calendarRef = useRef(null);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [checkedDates, setCheckedDates] = useState([]);
  const [selectedWeekdays, setSelectedWeekdays] = useState([]);

  useEffect(() => {
    checkUncheckWeekDay(checkedDates);
    checkUncheckSelectAll(checkedDates);
  }, [checkedDates]);

  const checkDateInRange = (minDate, today, maxDate) => {
    return !minDate.isBefore(today) && !minDate.isAfter(maxDate);
  };

  const getCurrentMonthDates = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return [];

    const view = calendarApi.view;
    const monthStart = dayjs(view.currentStart).tz().startOf("month");
    const monthEnd = monthStart.endOf("month");
    const today = dayjs().tz().startOf("day");
    const maxDate = today.add(3, "month").startOf("day");

    const validDates = [];
    let d = dayjs(monthStart);
    while (!d.isAfter(monthEnd)) {
      if (!d.isBefore(today) && !d.isAfter(maxDate)) {
        validDates.push(d.format("YYYY-MM-DD"));
      }
      d = d.add(1, "day");
    }
    return validDates;
  };

  const getSequenceById = (id) => sequences.find((seq) => seq.id === id);

  const createEvent = (sequence, date) => ({
    id: `${sequence.id}-${date}`,
    title: sequence.name,
    date,
    sequenceId: sequence.id,
    backgroundColor: sequence.backgroundColor,
    borderColor: sequence.borderColor,
    textColor: sequence.textColor,
  });

  const checkUncheckSelectAll = (selectedDates) => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;

    const view = calendarApi.view;
    const currentMonthStart = dayjs(view.currentStart).tz().startOf("month");
    const currentMonthEnd = currentMonthStart.endOf("month");
    const today = dayjs().tz().startOf("day");
    const maxDate = today.add(3, "month").startOf("day");

    const allValidDates = [];
    let d = currentMonthStart;
    while (!d.isAfter(currentMonthEnd)) {
      if (checkDateInRange(d, today, maxDate)) {
        allValidDates.push(d.format("YYYY-MM-DD"));
      }
      d = d.add(1, "day");
    }

    const allChecked =
      allValidDates.length > 0 &&
      allValidDates.every((d) => selectedDates.includes(d));
    setIsSelectAll(allChecked);
  };

  const checkUncheckWeekDay = (checkedDayDates) => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;

    const view = calendarApi.view;
    const currentMonthStart = dayjs(view.currentStart).tz().startOf("month");
    const currentMonthEnd = currentMonthStart.endOf("month");
    const today = dayjs().tz().startOf("day");
    const maxDate = today.add(3, "month").startOf("day");

    const newSelectedWeekdays = [];

    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      const datesForDay = [];

      let d = dayjs(currentMonthStart);
      while (!d.isAfter(currentMonthEnd)) {
        if (d.day() === dayIndex && checkDateInRange(d, today, maxDate)) {
          datesForDay.push(d.format("YYYY-MM-DD"));
        }
        d = d.add(1, "day");
      }

      const allChecked =
        datesForDay.length > 0 &&
        datesForDay.every((d) => checkedDayDates.includes(d));
      if (allChecked) newSelectedWeekdays.push(dayIndex);
    }

    setSelectedWeekdays(newSelectedWeekdays);
  };

  const handleDayHeaderCheckbox = (dayIndex) => {
    const sequence = getSequenceById(selectedSequenceId);
    if (!sequence) return;

    const allDates = getCurrentMonthDates();
    const matchingDates = allDates.filter(
      (date) => dayjs(date).day() === dayIndex
    );
    const isSelected = selectedWeekdays.includes(dayIndex);

    if (isSelected) {
      const removedDates = events
        .filter(
          (event) =>
            matchingDates.includes(event.date) &&
            event.sequenceId === sequence.id
        )
        .map((e) => e.date);

      setEvents((prev) =>
        prev.filter(
          (event) =>
            !(
              matchingDates.includes(event.date) &&
              event.sequenceId === sequence.id
            )
        )
      );
      setCheckedDates((prev) => prev.filter((d) => !removedDates.includes(d)));
      setSelectedWeekdays((prev) => prev.filter((d) => d !== dayIndex));
    } else {
      const newEvents = [];
      const newChecked = [];

      for (const date of matchingDates) {
        const exists = events.find((e) => e.date === date);
        if (!exists) {
          newEvents.push(createEvent(sequence, date));
          newChecked.push(date);
        }
      }

      setEvents((prev) => [...prev, ...newEvents]);
      setCheckedDates((prev) => [...new Set([...prev, ...newChecked])]);
      setSelectedWeekdays((prev) => [...prev, dayIndex]);
    }
  };

  const handleSelectAllToggle = () => {
    const sequence = getSequenceById(selectedSequenceId);
    if (!sequence) return;

    const allDates = getCurrentMonthDates();

    if (isSelectAll) {
      const sequenceOwnedDates = events
        .filter(
          (event) =>
            allDates.includes(event.date) && event.sequenceId === sequence.id
        )
        .map((e) => e.date);
      const newEvents = events.filter(
        (event) =>
          !(
            sequenceOwnedDates.includes(event.date) &&
            event.sequenceId === sequence.id
          )
      );

      setEvents(newEvents);
      setCheckedDates((prev) =>
        prev.filter((d) => !sequenceOwnedDates.includes(d))
      );
    } else {
      const newEvents = [];
      const newChecked = [];

      for (const date of allDates) {
        const exists = events.find((e) => e.date === date);
        if (!exists) {
          newEvents.push(createEvent(sequence, date));
          newChecked.push(date);
        }
      }

      setEvents((prev) => [...prev, ...newEvents]);
      setCheckedDates((prev) => [...new Set([...prev, ...newChecked])]);
    }

    setIsSelectAll((prev) => !prev);
  };

  const toggleIndividualDate = (dateStr) => {
    const isChecked = checkedDates.includes(dateStr);
    const updatedDates = isChecked
      ? checkedDates.filter((d) => d !== dateStr)
      : [...checkedDates, dateStr];

    setCheckedDates(updatedDates);
    checkUncheckSelectAll(updatedDates);

    const sequence = getSequenceById(selectedSequenceId);
    if (!sequence) return;

    const existingEvent = events.find((e) => e.date === dateStr);

    if (!isChecked && !existingEvent) {
      setEvents((prev) => [...prev, createEvent(sequence, dateStr)]);
    }

    if (isChecked && existingEvent?.sequenceId === sequence.id) {
      setEvents((prev) => prev.filter((event) => event.date !== dateStr));
    }
  };

  const renderDayCell = (arg) => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;

    const view = calendarApi.view;
    const cellDate = dayjs(arg.date).tz().startOf("day");
    const monthStart = dayjs(view.currentStart).tz().startOf("month");
    const today = dayjs().tz().startOf("day");
    const maxDate = today.add(3, "month").startOf("day");

    const dateStr = cellDate.format("YYYY-MM-DD");
    const isInRange = checkDateInRange(cellDate, today, maxDate);

    const existingEvent = events.find((event) => event.date === dateStr);
    const isDifferentEvent =
      existingEvent && existingEvent.sequenceId !== selectedSequenceId;

    const isChecked = checkedDates.includes(dateStr) || Boolean(existingEvent);

    return (
      <div className="fc-day-checkbox-wrapper" style={{ position: "relative" }}>
        {enableEdit && selectedSequenceId && isInRange ? (
          <div className="fc-day-row">
            <Checkbox
              checked={isChecked}
              disabled={
                !cellDate.isSame(monthStart, "month") || isDifferentEvent
              }
              onChange={() => toggleIndividualDate(dateStr)}
            >
              {arg.dayNumberText}
            </Checkbox>

            {isDifferentEvent && (
              <CloseOutlined
                onClick={(e) => {
                  e.stopPropagation(); // prevent calendar click propagation
                  if (
                    cellDate.isSame(monthStart, "month")
                  ) {
                    toggleIndividualDate(dateStr);
                    setEvents((prev) =>
                      prev.filter((event) => event.date !== dateStr)
                    );
                  }
                }}
                style={{
                  fontSize: 12,
                  color: "#ff4d4f",
                  cursor: "pointer",
                }}
              />
            )}
          </div>
        ) : (
          <div>{arg.dayNumberText}</div>
        )}
      </div>
    );
  };

  const renderDayHeader = (arg) => {
    const weekday = dayjs(arg.date).tz().day();
    const isSelected = selectedWeekdays.includes(weekday);

    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        {enableEdit && selectedSequenceId ? (
          <Checkbox
            checked={isSelected}
            onChange={() => handleDayHeaderCheckbox(weekday)}
          >
            {arg.text}
          </Checkbox>
        ) : (
          <span>{arg.text}</span>
        )}
      </div>
    );
  };

  const handleDatesSet = () => {
    checkUncheckWeekDay(checkedDates);
    checkUncheckSelectAll(checkedDates);
  };

  return (
    <>
      {enableEdit && selectedSequenceId && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: 12,
          }}
          className="fc-calendar-select-all"
        >
          <Checkbox checked={isSelectAll} onChange={handleSelectAllToggle}>
            Select All Days
          </Checkbox>
        </div>
      )}
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        headerToolbar={{
          left: "prev",
          center: "title",
          right: "next",
        }}
        timeZone="Asia/Kolkata"
        events={events}
        dayCellContent={renderDayCell}
        dayHeaderContent={renderDayHeader}
        datesSet={handleDatesSet}
      />
    </>
  );
};

export default ScheduleCalendar;
